/**
 * Bundle Quick-Build — smellawear cart drawer and collection pages
 *
 * Zweck: ein Bundle direkt IM Cart-Drawer konfigurieren und hinzufügen, ohne
 * die Seite zu verlassen. Ersetzt den bisherigen Weg "Upsell-Karte → Link auf
 * die PDP → dort den Bundle-Picker bedienen".
 *
 * Aufbau:
 *   1. Rail   — [data-bqb-rail]  horizontale Snap-Leiste mit Empfehlungen.
 *               Kein Scroll-Handler, kein JS-Karussell: das Scrollen macht der
 *               Browser, JS spiegelt nur die Position in die Fortschritts-
 *               segmente (IntersectionObserver) und wärmt Produktdaten vor.
 *   2. Sheet  — [data-bqb-sheet] ein <dialog> pro Oberfläche, wird aus dem
 *               data-Payload der angetippten Karte befüllt. Karten können ihr
 *               Sheet mit data-bqb-sheet-id gezielt adressieren.
 *
 * Datenherkunft (bewusst zweistufig, siehe README "all_products-Limit"):
 *   - Slot-Struktur + Anzeigenamen kommen aus dem Karten-JSON (Liquid,
 *     product.metafields.custom.bundle_konfiguration) → Sheet öffnet ohne Fetch
 *   - Varianten-IDs, Bilder und Verfügbarkeit kommen aus /products/<handle>.js
 *     mit demselben sessionStorage-Namespace `bp:p:<handle>` wie
 *     extensions/bundle-picker/assets/bundle-picker.js (TTL 5 min)
 *
 * Add-to-Cart ist exakt das Format aus bundle-picker.js (Single-Item + top-level
 * `sections`), damit cart-drawer.renderContents(data) direkt funktioniert.
 */

(function () {
  "use strict";

  const CACHE_TTL_MS = 5 * 60 * 1000;
  const DEFAULT_COPY = {
    change: "ändern",
    adding: "Wird hinzugefügt …",
    added: "Zum Warenkorb hinzugefügt",
    add: "Bundle hinzufügen",
    retry: "Nochmal versuchen",
    soldOut: "ausverkauft",
    soldOutSlot: (title) => `${title} ist gerade ausverkauft — wir haben auf die nächste Option gewechselt.`,
    addFailed: "Das Bundle konnte nicht hinzugefügt werden. Dein Warenkorb ist unverändert.",
    loadFailed: "Die Auswahl konnte nicht geladen werden. Öffne das Bundle auf der Produktseite.",
    announceSelected: (label, title) => `${label}: ${title} gewählt`,
    choose: "Jetzt auswählen",
    progress: (selected, total) => `${selected} von ${total} ausgewählt`,
    completeFirst: "Vervollständige dein Bundle",
    complete: (percent) => `Bundle Komplett - ${percent}% gespart`,
    chooseMany: (count) => `Wähle ${count} Varianten aus`,
    chooseOne: "Noch 1 Variante auswählen",
    familyDeal: "🎁 Family-Deal: Nimm 5 & krieg 1 gratis →",
    selectionHint: "Wähle zuerst eine Variante aus.",
    selectionProperty: "Deine Auswahl",
  };

  function optionAccent(handle, title = "") {
    const key = `${handle} ${title}`.toLowerCase();
    if (key.includes("azura") || key.includes("blue") || key.includes("blau")) return "#8faec2";
    if (key.includes("verdan") || key.includes("green") || key.includes("grün")) return "#74775f";
    if (key.includes("rosayn") || key.includes("rose") || key.includes("pink")) return "#d8a8ba";
    if (key.includes("noctura") || key.includes("black") || key.includes("schwarz")) return "#292929";
    if (key.includes("white") || key.includes("weiß") || key.includes("weiss")) return "#c7c7c2";
    if (key.includes("ghost") || key.includes("silver") || key.includes("silber")) return "#a9aaa5";
    return "#8b6f47";
  }

  // ── Produktdaten ──────────────────────────────────────────────────────────

  function cacheGet(handle) {
    try {
      const raw = sessionStorage.getItem(`bp:p:${handle}`);
      if (!raw) return null;
      const { t, d } = JSON.parse(raw);
      return Date.now() - t < CACHE_TTL_MS ? d : null;
    } catch {
      return null;
    }
  }

  function cacheSet(handle, data) {
    try {
      sessionStorage.setItem(`bp:p:${handle}`, JSON.stringify({ t: Date.now(), d: data }));
    } catch {
      /* Private Mode / Quota — Cache ist optional, nie kritisch */
    }
  }

  function fetchProductWithXHR(url, signal) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open("GET", url, true);
      request.responseType = "json";
      request.onload = () => resolve(request.status >= 200 && request.status < 300 ? request.response : null);
      request.onerror = () => resolve(null);
      request.onabort = () => reject(new DOMException("Aborted", "AbortError"));
      signal?.addEventListener("abort", () => request.abort(), { once: true });
      request.send();
    });
  }

  async function fetchProduct(handle, { fresh = false, signal } = {}) {
    if (!fresh) {
      const cached = cacheGet(handle);
      if (cached) return cached;
    }
    const url = `/products/${handle}.js`;
    try {
      let data = null;
      if (typeof window.fetch === "function") {
        const resp = await window.fetch(url, { signal });
        if (resp.ok) data = await resp.json();
      }
      // Some storefront preview runtimes remove fetch from the page global.
      // XHR keeps Quick Build functional there without changing the data flow.
      if (!data) data = await fetchProductWithXHR(url, signal);
      if (!data) return null;
      cacheSet(handle, data);
      return data;
    } catch (error) {
      if (error.name === "AbortError") throw error;
      return null;
    }
  }

  function optionFrom(product) {
    const variant = product?.variants?.[0];
    if (!variant) return null;
    return {
      variantGid: `gid://shopify/ProductVariant/${variant.id}`,
      image: variant.featured_image?.src || product.featured_image || "",
      available: variant.available !== false,
      title: product.title || "",
    };
  }

  // ── Bottom-Sheet ──────────────────────────────────────────────────────────

  class QuickBuildSheet {
    #controller = null;
    #trigger = null;
    #bundle = null;
    #slots = [];        // [{ label, handles:[], choice: handle, hydrated: bool }]
    #products = {};     // handle → { variantGid, image, available, title }
    #activeSlot = -1;
    #advanceTimer = 0;

    constructor(root) {
      this.root = root;
      const copy = root.dataset;
      this.money = new Intl.NumberFormat(copy.bqbLocale || document.documentElement.lang || "de-DE", {
        style: "currency",
        currency: copy.bqbCurrency || window.Shopify?.currency?.active || "EUR",
      });
      this.copy = {
        change: copy.bqbCopyChange || DEFAULT_COPY.change,
        adding: copy.bqbCopyAdding || DEFAULT_COPY.adding,
        added: copy.bqbCopyAdded || DEFAULT_COPY.added,
        add: copy.bqbCopyAdd || DEFAULT_COPY.add,
        retry: copy.bqbCopyRetry || DEFAULT_COPY.retry,
        soldOut: copy.bqbCopySoldOut || DEFAULT_COPY.soldOut,
        soldOutSlot: (title) =>
          (copy.bqbCopySoldOutSlot || DEFAULT_COPY.soldOutSlot("[title]")).replace("[title]", title),
        addFailed: copy.bqbCopyAddFailed || DEFAULT_COPY.addFailed,
        loadFailed: copy.bqbCopyLoadFailed || DEFAULT_COPY.loadFailed,
        announceSelected: (label, title) =>
          (copy.bqbCopySelected || DEFAULT_COPY.announceSelected("[label]", "[title]"))
            .replace("[label]", label)
            .replace("[title]", title),
        save: (amount) => (copy.bqbCopySave || `Du sparst [amount]`).replace("[amount]", amount),
        choose: copy.bqbCopyChoose || DEFAULT_COPY.choose,
        progress: (selected, total) =>
          (copy.bqbCopyProgress || DEFAULT_COPY.progress("[selected]", "[total]"))
            .replace("[selected]", selected)
            .replace("[total]", total),
        completeFirst: copy.bqbCopyCompleteFirst || DEFAULT_COPY.completeFirst,
        complete: (percent) =>
          (copy.bqbCopyComplete || DEFAULT_COPY.complete("[percent]")).replace("[percent]", percent),
        chooseMany: (count) =>
          (copy.bqbCopyChooseMany || DEFAULT_COPY.chooseMany("[count]")).replace("[count]", count),
        chooseOne: copy.bqbCopyChooseOne || DEFAULT_COPY.chooseOne,
        familyDeal: copy.bqbCopyFamilyDeal || DEFAULT_COPY.familyDeal,
        selectionHint: copy.bqbCopySelectionHint || DEFAULT_COPY.selectionHint,
        selectionProperty: copy.bqbSelectionProperty || DEFAULT_COPY.selectionProperty,
      };
      this.track = root.querySelector("[data-bqb-track]");
      this.paneSummary = root.querySelector("[data-bqb-pane-summary]");
      this.paneOptions = root.querySelector("[data-bqb-pane-options]");
      this.slotList = root.querySelector("[data-bqb-slot-list]");
      this.optionList = root.querySelector("[data-bqb-option-list]");
      this.heading = root.querySelector("[data-bqb-heading]");
      this.backBtn = root.querySelector("[data-bqb-back]");
      this.cta = root.querySelector("[data-bqb-cta]");
      this.ctaLabel = root.querySelector("[data-bqb-cta-label]");
      this.totalEl = root.querySelector("[data-bqb-total]");
      this.savingEl = root.querySelector("[data-bqb-saving]");
      this.errorEl = root.querySelector("[data-bqb-error]");
      this.liveEl = root.querySelector("[data-bqb-live]");
      this.viewport = root.querySelector("[data-bqb-viewport]");
      this.progress = root.querySelector("[data-bqb-progress]");
      this.progressLabel = root.querySelector("[data-bqb-progress-label]");
      this.progressCount = root.querySelector("[data-bqb-progress-count]");
      this.progressBar = root.querySelector("[data-bqb-progress-bar]");
      this.familySwitch = root.querySelector("[data-bqb-family-switch]");

      root.addEventListener("close", () => this.#onClose());
      root.querySelector("[data-bqb-close]")?.addEventListener("click", () => root.close());
      this.backBtn?.addEventListener("click", () => this.#goBack());
      this.cta?.addEventListener("click", () => this.#confirm());
      this.familySwitch?.addEventListener("click", () => this.#switchToUpgrade());
      this.optionList?.addEventListener("keydown", (event) => this.#onOptionKeydown(event));
    }

    #payloadFrom(card) {
      try {
        return JSON.parse(card.querySelector("[data-bqb-payload]").textContent);
      } catch {
        return null;
      }
    }

    #loadBundle(payload, { preserveChoices = false } = {}) {
      const previousChoices = preserveChoices ? this.#slots.map((slot) => slot.choice) : [];
      this.#bundle = payload;
      this.#products = payload.products || {};
      this.#activeSlot = -1;
      this.#slots = payload.slots.map((slot, index) => {
        const previousChoice = previousChoices[index];
        return {
          label: slot.label,
          handles: slot.handles.slice(),
          titles: slot.titles || {},
          choice: previousChoice && slot.handles.includes(previousChoice) ? previousChoice : null,
          hydrated: slot.handles.every((handle) => Boolean(this.#products[handle])),
        };
      });

      this.heading.textContent = payload.title;
      this.#setError("");
      this.#renderSummary();
      this.#renderTotals();
      this.#renderProgress();
      this.#showSummary({ animate: false });
      if (this.familySwitch) {
        this.familySwitch.textContent = this.copy.familyDeal;
        this.familySwitch.hidden = !(
          payload.handle === "duo-bundle" && payload.upgradeHandle === "family-bundle"
        );
      }
    }

    open(card, trigger = card) {
      const payload = this.#payloadFrom(card);
      if (!payload) return false; // kein Payload → Produktlink bleibt der Fallback

      this.#trigger = trigger;
      this.#loadBundle(payload);

      this.root.showModal();
      // Höhe einmalig fixieren, damit der Pane-Wechsel die Sheet-Oberkante
      // nicht verschiebt (Layout-Sprung wäre der einzige sichtbare Jank hier).
      this.viewport.style.minBlockSize = `${this.paneSummary.scrollHeight}px`;

      const firstIncomplete = this.#slots.findIndex((slot) => !slot.choice);
      if (firstIncomplete >= 0) this.#showOptions(firstIncomplete);
      this.#hydrate();
      return true;
    }

    #switchToUpgrade() {
      const handle = this.#bundle?.upgradeHandle;
      if (!handle) return;
      const collectionRoot = this.root.closest("smellawear-collection");
      const card = collectionRoot?.querySelector(
        `[data-sw-bundle-card][data-bqb-handle="${CSS.escape(handle)}"]`
      );
      const payload = card ? this.#payloadFrom(card) : null;
      if (!payload) return;

      this.root.classList.remove("bqb-sheet--switching");
      void this.root.offsetWidth;
      this.root.classList.add("bqb-sheet--switching");
      this.#loadBundle(payload, { preserveChoices: true });
      this.viewport.style.minBlockSize = `${this.paneSummary.scrollHeight}px`;
      const firstIncomplete = this.#slots.findIndex((slot) => !slot.choice);
      if (firstIncomplete >= 0) this.#showOptions(firstIncomplete);
      this.#hydrate();

      if (!this.savingEl.hidden) {
        this.savingEl.classList.remove("bqb-sheet__saving--highlight");
        void this.savingEl.offsetWidth;
        this.savingEl.classList.add("bqb-sheet__saving--highlight");
      }
      this.#announce(`${payload.title}: ${this.copy.progress(this.#slots.filter((slot) => slot.choice).length, this.#slots.length)}`);
    }

    async #hydrate() {
      this.#controller?.abort();
      this.#controller = new AbortController();
      const signal = this.#controller.signal;
      const handles = [...new Set(this.#slots.flatMap((slot) => slot.handles))].filter(
        (handle) => !this.#products[handle]
      );

      try {
        await Promise.all(
          handles.map(async (handle) => {
            const product = await fetchProduct(handle, { signal });
            const option = optionFrom(product);
            if (option) this.#products[handle] = option;
          })
        );
      } catch {
        return; // abgebrochen, weil das Sheet inzwischen zu ist
      }

      for (const slot of this.#slots) {
        slot.hydrated = true;
        if (slot.choice) {
          const chosen = this.#products[slot.choice];
          if (!chosen?.variantGid || !chosen.available) {
            this.#setError(this.copy.soldOutSlot(this.#titleOf(slot, slot.choice)));
            slot.choice = null;
          }
        }
      }

      this.#renderSummary();
      this.#renderProgress();
      if (this.#activeSlot >= 0) this.#renderOptions(this.#activeSlot);
      if (this.#slots.some((slot) => !slot.handles.some((handle) => this.#products[handle]?.available))) {
        this.#setError(this.copy.loadFailed);
      }
    }

    #titleOf(slot, handle) {
      if (!handle) return this.copy.choose;
      const curatedTitle = slot.titles[handle];
      if (curatedTitle && curatedTitle !== handle) return curatedTitle;
      return this.#products[handle]?.title || curatedTitle || handle;
    }

    #renderSummary() {
      this.slotList.innerHTML = "";
      this.#slots.forEach((slot, index) => {
        const item = document.createElement("li");
        const button = document.createElement("button");
        button.type = "button";
        button.className = "bqb-slot";
        button.dataset.bqbSlot = String(index);
        const title = this.#titleOf(slot, slot.choice);
        button.setAttribute("aria-label", `${slot.label}: ${title} — ${this.copy.change}`);

        const image = this.#products[slot.choice]?.image;
        button.innerHTML = `
          ${image
            ? `<img class="bqb-slot__thumb" src="${image}" alt="" width="40" height="40" loading="lazy">`
            : '<span class="bqb-slot__thumb bqb-slot__thumb--empty" aria-hidden="true"></span>'}
          <span class="bqb-slot__text">
            <span class="bqb-slot__label">${escapeHtml(slot.label)}</span>
            <span class="bqb-slot__value${slot.choice ? "" : " bqb-slot__value--empty"}">${escapeHtml(title)}</span>
          </span>
          <span class="bqb-slot__change" aria-hidden="true">${slot.choice ? this.copy.change : "→"}</span>
        `;
        button.addEventListener("click", () => this.#showOptions(index));
        item.appendChild(button);
        this.slotList.appendChild(item);
      });
    }

    #renderOptions(slotIndex) {
      const slot = this.#slots[slotIndex];
      this.optionList.innerHTML = "";
      this.optionList.setAttribute("aria-label", slot.label);

      slot.handles.forEach((handle) => {
        const data = this.#products[handle];
        const checked = handle === slot.choice;
        const disabled = slot.hydrated && !data?.available;

        const button = document.createElement("button");
        button.type = "button";
        button.className = "bqb-option";
        button.role = "radio";
        button.dataset.bqbOption = handle;
        button.style.setProperty("--bqb-option-accent", optionAccent(handle, this.#titleOf(slot, handle)));
        button.setAttribute("aria-checked", String(checked));
        button.setAttribute("aria-disabled", String(disabled));
        button.tabIndex = checked || (!slot.choice && !disabled && !this.optionList.querySelector('[tabindex="0"]')) ? 0 : -1;
        button.innerHTML = `
          <img class="bqb-option__thumb" src="${data?.image || ""}" alt="" width="40" height="40" loading="lazy">
          <span class="bqb-option__name">${escapeHtml(this.#titleOf(slot, handle))}</span>
          ${disabled ? `<span class="bqb-option__note">${this.copy.soldOut}</span>` : ""}
        `;
        button.addEventListener("click", () => {
          if (disabled) return;
          this.#choose(slotIndex, handle, button);
        });
        this.optionList.appendChild(button);
      });
    }

    #choose(slotIndex, handle, selectedButton) {
      window.clearTimeout(this.#advanceTimer);
      const slot = this.#slots[slotIndex];
      slot.choice = handle;
      this.#setError("");
      this.optionList.querySelectorAll("[data-bqb-option]").forEach((button) => {
        button.setAttribute("aria-checked", String(button === selectedButton));
        button.classList.toggle("bqb-option--confirmed", button === selectedButton);
      });
      this.#renderSummary();
      this.#renderProgress();
      this.#announce(this.copy.announceSelected(slot.label, this.#titleOf(slot, handle)));

      const advance = () => {
        const nextSlot = this.#slots.findIndex((candidate, index) => index > slotIndex && !candidate.choice);
        if (nextSlot >= 0) this.#showOptions(nextSlot);
        else this.#showSummary();
      };
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduced) advance();
      else this.#advanceTimer = window.setTimeout(advance, 260);
    }

    #onOptionKeydown(event) {
      const keys = ["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp"];
      if (!keys.includes(event.key)) return;
      const buttons = [...this.optionList.querySelectorAll("[data-bqb-option]")];
      const current = buttons.indexOf(document.activeElement);
      if (current < 0) return;
      event.preventDefault();
      const step = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : -1;
      const next = buttons[(current + step + buttons.length) % buttons.length];
      next.focus();
    }

    #showOptions(slotIndex) {
      this.#activeSlot = slotIndex;
      this.#renderOptions(slotIndex);
      this.track.dataset.bqbPane = "options";
      this.paneSummary.setAttribute("aria-hidden", "true");
      this.paneOptions.setAttribute("aria-hidden", "false");
      this.backBtn.hidden = false;
      this.heading.textContent = this.#slots[slotIndex].label;
      const focusTarget = this.optionList.querySelector('[aria-checked="true"]') || this.optionList.querySelector('[aria-disabled="false"]');
      focusTarget?.focus();
    }

    #goBack() {
      if (this.#activeSlot > 0) {
        this.#showOptions(this.#activeSlot - 1);
        return;
      }
      this.#showSummary();
    }

    #showSummary({ animate = true } = {}) {
      if (!animate) this.track.style.transition = "none";
      this.track.dataset.bqbPane = "summary";
      this.paneSummary.setAttribute("aria-hidden", "false");
      this.paneOptions.setAttribute("aria-hidden", "true");
      this.backBtn.hidden = true;
      this.heading.textContent = this.#bundle.title;
      if (!animate) requestAnimationFrame(() => (this.track.style.transition = ""));
      if (this.#activeSlot >= 0) {
        this.slotList.querySelector(`[data-bqb-slot="${this.#activeSlot}"]`)?.focus();
        this.#activeSlot = -1;
      }
    }

    #renderTotals() {
      const price = this.#bundle.price / 100;
      const regular = this.#bundle.regular / 100;
      const formattedPrice = this.money.format(price).replace(/[\u00a0\u202f]/g, " ");
      this.totalEl.textContent = formattedPrice;
      if (regular > price) {
        const formattedSaving = this.money.format(regular - price).replace(/[\u00a0\u202f]/g, " ");
        this.savingEl.textContent = this.copy.save(formattedSaving);
        this.savingEl.hidden = false;
      } else {
        this.savingEl.hidden = true;
      }
    }

    #renderProgress() {
      const selected = this.#slots.filter((slot) => Boolean(slot.choice)).length;
      const total = this.#slots.length;
      const complete = total > 0 && selected === total;
      const progressCopy = this.copy.progress(selected, total);
      const regular = Number(this.#bundle.regular) || 0;
      const price = Number(this.#bundle.price) || 0;
      const savingPercent = regular > price ? Math.round(((regular - price) / regular) * 100) : 0;
      this.progress?.setAttribute("aria-valuemax", String(total || 1));
      this.progress?.setAttribute("aria-valuenow", String(selected));
      this.progress?.setAttribute("aria-valuetext", progressCopy);
      this.progress?.classList.toggle("bqb-progress--complete", complete);
      if (this.progressLabel) {
        this.progressLabel.textContent = complete ? this.copy.complete(savingPercent) : this.copy.completeFirst;
      }
      if (this.progressCount) this.progressCount.textContent = complete ? "" : progressCopy;
      const ratio = total ? selected / total : 0;
      if (this.progressBar) this.progressBar.style.setProperty("--bqb-progress", String(ratio));
      this.#setCta("idle");
    }

    #setCta(state) {
      this.cta.dataset.bqbState = state;
      const selected = this.#slots.filter((slot) => Boolean(slot.choice)).length;
      const remaining = Math.max(0, this.#slots.length - selected);
      this.root.classList.toggle("bqb-sheet--ready", remaining === 0 && state === "idle");
      this.cta.disabled = state === "busy" || state === "done";
      this.cta.setAttribute("aria-busy", String(state === "busy"));
      this.ctaLabel.textContent =
        state === "busy"
          ? this.copy.adding
          : state === "done"
            ? this.copy.added
            : state === "error"
              ? this.copy.retry
              : remaining > 1
                ? this.copy.chooseMany(remaining)
                : remaining === 1
                  ? this.copy.chooseOne
                  : `${this.copy.add} • ${this.totalEl.textContent}`;
    }

    #setError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    #announce(message) {
      this.liveEl.textContent = message;
    }

    #nudgeSelection() {
      const firstIncomplete = this.#slots.findIndex((slot) => !slot.choice);
      if (firstIncomplete < 0) return;
      if (this.#activeSlot !== firstIncomplete) this.#showOptions(firstIncomplete);
      this.optionList.classList.remove("bqb-options--nudge");
      void this.optionList.offsetWidth;
      this.optionList.classList.add("bqb-options--nudge");
      window.setTimeout(() => this.optionList.classList.remove("bqb-options--nudge"), 420);
      this.#announce(this.copy.selectionHint);
    }

    async #confirm() {
      if (this.#slots.some((slot) => !slot.choice)) {
        this.#setError("");
        this.#nudgeSelection();
        return;
      }
      this.#setError("");
      this.#setCta("busy");

      try {
        const handles = this.#slots.map((slot) => slot.choice);
        // Verfügbarkeit unmittelbar vor dem Add erneut prüfen (fresh) — gleiche
        // Regel wie im PDP-Picker, sonst landen ausverkaufte Slots im Cart.
        const fresh = {};
        await Promise.all(
          [...new Set(handles)].map(async (handle) => {
            const product = await fetchProduct(handle, { fresh: true });
            const option = optionFrom(product);
            if (option) fresh[handle] = option;
          })
        );

        for (const [index, handle] of handles.entries()) {
          const option = fresh[handle] || this.#products[handle];
          if (!option?.variantGid) throw new Error(this.copy.loadFailed);
          if (!option.available) {
            this.#products[handle] = option;
            this.#slots[index].hydrated = true;
            this.#renderSummary();
            throw new Error(this.copy.soldOutSlot(this.#titleOf(this.#slots[index], handle)));
          }
          this.#products[handle] = option;
        }

        const data = await addBundle({
          shellVariantId: this.#bundle.variantId,
          bundleHandle: this.#bundle.handle,
          slots: this.#slots.map((slot) => ({
            variantGid: this.#products[slot.choice].variantGid,
            title: this.#titleOf(slot, slot.choice),
          })),
          selectionProperty: this.copy.selectionProperty,
        }, this.copy.addFailed);

        this.#setCta("done");
        this.#announce(this.copy.added);
        this.#finish(data);
      } catch (error) {
        this.#setCta("error");
        this.#setError(error.message || this.copy.addFailed);
      }
    }

    /**
     * Erfolg: Sheet fährt weg WÄHREND der Drawer dahinter neu rendert. Der
     * Kunde sieht dadurch eine einzige Bewegung statt "Sheet zu, Blitz, neue
     * Liste".
     */
    #finish(data) {
      this.root.classList.add("bqb-sheet--done");
      const done = () => {
        this.root.classList.remove("bqb-sheet--done");
        // renderContents ersetzt gleich das komplette Drawer-Markup inklusive
        // der auslösenden Karte — der Fokus darf also NICHT dorthin zurück,
        // sonst landet er auf einem entfernten Element (= auf <body>).
        this.#trigger = null;
        this.root.close();
        const drawer = document.querySelector("cart-drawer") || document.querySelector("cart-notification");
        if (drawer && typeof drawer.renderContents === "function") {
          drawer.renderContents(data);
          flashNewLine(data?.key);
          restoreFocusAfterRender();
        } else {
          document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
        }
        document.dispatchEvent(
          new CustomEvent("bundle:added", { detail: { handle: this.#bundle.handle, source: "quick-build" } })
        );
      };
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduced) {
        done();
        return;
      }
      // Sicherheitsnetz: feuert animationend nicht (unterdrückte Animationen,
      // Hintergrund-Tab, CSS nicht geladen), bliebe das Sheet über einem
      // bereits erfolgreichen Add offen stehen — der schlimmste Zustand hier.
      let finished = false;
      const once = () => {
        if (finished) return;
        finished = true;
        done();
      };
      this.root.addEventListener("animationend", once, { once: true });
      setTimeout(once, 400);
    }

    #onClose() {
      window.clearTimeout(this.#advanceTimer);
      this.#advanceTimer = 0;
      this.#controller?.abort();
      this.#controller = null;
      // <dialog> stellt den Fokus beim Schließen selbst wieder her — und zwar
      // NACH diesem Handler. Ein synchrones focus() hier würde davon wieder
      // überschrieben, deshalb erst im nächsten Frame.
      const trigger = this.#trigger;
      this.#trigger = null;
      if (trigger) requestAnimationFrame(() => trigger.focus({ preventScroll: true }));
    }
  }

  // ── Add-to-Cart ───────────────────────────────────────────────────────────

  async function addBundle({ shellVariantId, bundleHandle, slots, selectionProperty }, addFailedCopy = DEFAULT_COPY.addFailed) {
    const properties = { _bundle_handle: bundleHandle };
    slots.forEach((slot, index) => {
      properties[`_slot_${index + 1}_variant`] = slot.variantGid;
      if (slot.title) properties[`_slot_${index + 1}_title`] = slot.title;
    });
    const publicKey = selectionProperty || DEFAULT_COPY.selectionProperty;
    properties[publicKey] = slots.map((slot) => slot.title).filter(Boolean).join(" · ");
    properties._bundle_selection_public_key = publicKey;

    // Single-Item-Format (nicht items[]): Response hat top-level `key` +
    // `sections` — genau das erwartet cart-drawer.renderContents().
    const body = { id: shellVariantId, quantity: 1, properties };
    const drawer = document.querySelector("cart-drawer") || document.querySelector("cart-notification");
    if (drawer && typeof drawer.getSectionsToRender === "function") {
      body.sections = drawer.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    const resp = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      const error = await resp.json().catch(() => ({}));
      throw new Error(error.description || addFailedCopy);
    }
    return resp.json();
  }

  function flashNewLine(key) {
    if (!key) return;
    requestAnimationFrame(() => {
      const line = document.querySelector(`[data-bqb-line-key="${CSS.escape(key)}"], [id*="${CSS.escape(key)}"]`);
      if (!line) return;
      line.classList.add("bqb-flash");
      setTimeout(() => line.classList.remove("bqb-flash"), 1000);
    });
  }

  // Nach dem Drawer-Re-Render einen sinnvollen Fokusanker suchen, statt den
  // Fokus auf <body> fallen zu lassen.
  function restoreFocusAfterRender() {
    requestAnimationFrame(() => {
      const anchor =
        document.querySelector("[data-bqb-track-scroll]") ||
        document.querySelector("cart-drawer [id*='CartDrawer-Heading']") ||
        document.querySelector("cart-drawer button");
      anchor?.focus?.({ preventScroll: true });
    });
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
  }

  // ── Rail ──────────────────────────────────────────────────────────────────

  function initRail(rail) {
    if (rail.dataset.bqbInit) return;
    rail.dataset.bqbInit = "1";

    const track = rail.querySelector("[data-bqb-track-scroll]");
    if (!track) return;

    // Beobachtet werden die Rail-EINTRÄGE, nicht die Quick-Build-Karten:
    // Einzelprodukt-Empfehlungen haben kein [data-bqb-card], sonst wären
    // Schritt-Index und Karten-Index gegeneinander verschoben.
    const items = [...rail.querySelectorAll(".bqb-rail__item")];
    const steps = [...rail.querySelectorAll("[data-bqb-step]")];
    const previousButton = rail.querySelector("[data-bqb-prev]");
    const nextButton = rail.querySelector("[data-bqb-next]");
    let activeIndex = 0;
    let scrollFrame = 0;

    function updateControls() {
      if (previousButton) previousButton.disabled = activeIndex <= 0;
      if (nextButton) nextButton.disabled = activeIndex >= items.length - 1;
    }

    function syncActiveIndex() {
      const trackStart = track.getBoundingClientRect().left;
      let nearestDistance = Number.POSITIVE_INFINITY;

      for (const [index, item] of items.entries()) {
        const distance = Math.abs(item.getBoundingClientRect().left - trackStart);
        if (distance >= nearestDistance) continue;
        nearestDistance = distance;
        activeIndex = index;
      }

      steps.forEach((step, index) => step.setAttribute("aria-current", String(index === activeIndex)));
      updateControls();
    }

    // Fortschritt spiegelt echte Scroll-Position (kein Timer, kein Auto-Wechsel).
    const visibility = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const index = items.indexOf(entry.target);
          if (index < 0 || !entry.isIntersecting) continue;
          // Produktdaten vorwärmen, sobald ein Eintrag sichtbar wird — der Tap
          // trifft dann fast immer einen warmen sessionStorage-Cache.
          warmCard(entry.target);
          if (entry.intersectionRatio > 0.6) {
            syncActiveIndex();
          }
        }
      },
      { root: track, threshold: [0, 0.61] }
    );
    items.forEach((item) => visibility.observe(item));

    steps.forEach((step, index) => {
      step.addEventListener("click", () => {
        scrollTrackToItem(track, items[index]);
      });
    });

    previousButton?.addEventListener("click", () => {
      activeIndex = Math.max(0, activeIndex - 1);
      scrollTrackToItem(track, items[activeIndex]);
      updateControls();
    });

    nextButton?.addEventListener("click", () => {
      activeIndex = Math.min(items.length - 1, activeIndex + 1);
      scrollTrackToItem(track, items[activeIndex]);
      updateControls();
    });

    track.addEventListener("scroll", () => {
      cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(syncActiveIndex);
    }, { passive: true });

    initRailDrag(track);
    syncActiveIndex();
  }

  // scrollIntoView() considers EVERY scrollable ancestor, not just the
  // nearest one — with the rail now living inside the cart's own vertical
  // scroll container (#CartDrawer-CartItems), calling it on a rail item was
  // visibly jumping/re-centering the whole cart on each auto-rotate tick,
  // even with block:"nearest". scrollTo() on the track itself only ever
  // touches the track's own scrollLeft, so it can't bleed into ancestors.
  function scrollTrackToItem(track, item) {
    if (!item) return;
    const trackRect = track.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const delta = itemRect.left - trackRect.left;
    track.scrollTo({ left: track.scrollLeft + delta, behavior: "smooth" });
  }

  /**
   * Maus-Drag-Scroll für Desktop: Touch scrollt die Track schon nativ
   * (overflow-x:auto + scroll-snap), aber Mauszeiger haben ohne das hier
   * keine Möglichkeit, seitlich zu wischen. Nur für Nicht-Touch-Pointer aktiv;
   * ein Schwellwert unterscheidet Drag von einem normalen Klick auf Karte/Button.
   */
  function initRailDrag(track) {
    const DRAG_THRESHOLD = 6; // px, danach zählt die Bewegung als Drag statt Klick
    let dragging = false;
    let dragged = false;
    let startX = 0;
    let startScrollLeft = 0;

    function suppressNextClick(event) {
      event.preventDefault();
      event.stopPropagation();
    }

    track.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "touch") return;
      dragging = true;
      dragged = false;
      startX = event.clientX;
      startScrollLeft = track.scrollLeft;
    });

    track.addEventListener("pointermove", (event) => {
      if (!dragging) return;
      const delta = event.clientX - startX;
      if (!dragged && Math.abs(delta) < DRAG_THRESHOLD) return;
      if (!dragged) {
        dragged = true;
        // Pointer-Capture erst NACH eindeutigem Drag setzen. Ein Capture
        // bereits beim pointerdown retargetet den normalen Button-Klick auf
        // die <ul>-Track und verhindert dadurch den Add-to-Cart-Handler.
        track.setPointerCapture(event.pointerId);
      }
      track.style.userSelect = "none";
      track.scrollLeft = startScrollLeft - delta;
    });

    function endDrag(event) {
      if (!dragging) return;
      dragging = false;
      track.style.userSelect = "";
      if (dragged) {
        // Der Klick, der nach dem Loslassen folgt, darf keine Karte/Sheet mehr
        // öffnen — sonst öffnet jeder Drag zusätzlich das Quick-Build-Sheet.
        track.addEventListener("click", suppressNextClick, { capture: true, once: true });
      }
      if (track.hasPointerCapture?.(event.pointerId)) {
        track.releasePointerCapture(event.pointerId);
      }
    }

    track.addEventListener("pointerup", endDrag);
    track.addEventListener("pointercancel", endDrag);
  }

  function warmCard(item) {
    if (item.dataset.bqbWarm) return;
    item.dataset.bqbWarm = "1";
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
    idle(() => {
      let payload;
      try {
        payload = JSON.parse(item.querySelector("[data-bqb-payload]").textContent);
      } catch {
        return; // Einzelprodukt-Karte ohne Payload — nichts vorzuwärmen
      }
      const handles = [...new Set(payload.slots.flatMap((slot) => slot.handles))];
      handles.forEach((handle) => fetchProduct(handle));
    });
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  const sheets = new WeakMap();

  function getSheet(card) {
    const collectionRoot = card?.closest("smellawear-collection");
    if (!collectionRoot) return null;
    const requestedId = card?.dataset.bqbSheetId;
    const requestedSheet = requestedId ? document.getElementById(requestedId) : null;
    const root =
      (requestedSheet && collectionRoot.contains(requestedSheet) && requestedSheet) ||
      collectionRoot.querySelector("[data-bqb-sheet]");
    if (!root) return null;
    if (!sheets.has(root)) sheets.set(root, new QuickBuildSheet(root));
    return sheets.get(root);
  }

  function addFailedCopy() {
    return document.querySelector("[data-bqb-sheet]")?.dataset.bqbCopyAddFailed || DEFAULT_COPY.addFailed;
  }

  // Der Rail liegt bereits innerhalb von #CartDrawer-Form. Deshalb darf der
  // Einzelprodukt-CTA kein verschachteltes <form> sein; er postet direkt per
  // Cart-AJAX und bleibt durch den global delegierten Listener rerender-sicher.
  async function addSingleProductToCart(variantId, button) {
    const errorElement = button.parentElement?.querySelector("[data-bcu-add-error]");
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    errorElement?.toggleAttribute("hidden", true);
    try {
      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", "1");
      const drawer = document.querySelector("cart-drawer");
      const sections = drawer?.getSectionsToRender?.().map((section) => section.id) || [
        "cart-drawer",
        "cart-icon-bubble",
      ];
      formData.append("sections", sections);
      formData.append("sections_url", window.location.pathname);

      const res = await fetch(window.routes?.cart_add_url || "/cart/add.js", {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest",
        },
      });
      const data = await res.json();
      if (!res.ok || data?.status) throw new Error(data?.description || data?.message || addFailedCopy());

      if (drawer && typeof drawer.renderContents === "function") {
        drawer.renderContents(data);
      } else {
        window.location.assign(window.routes?.cart_url || "/cart");
      }
    } catch (error) {
      if (errorElement) {
        errorElement.textContent = error?.message || addFailedCopy();
        errorElement.hidden = false;
      }
      console.error("Cart upsell add failed", error);
    } finally {
      button.disabled = false;
      button.removeAttribute("aria-busy");
    }
  }

  // Delegiert: der Drawer ersetzt bei jeder Cart-Änderung sein komplettes
  // Markup (renderContents) — ein globaler Listener überlebt das, pro-Element
  // gebundene Handler nicht (gleiches Muster wie cart-drawer-upsell.js).
  document.addEventListener("click", (event) => {
    if (!event.target.closest("smellawear-collection")) return;

    const singleButton = event.target.closest("[data-bcu-add-variant]");
    if (singleButton) {
      event.preventDefault();
      if (!singleButton.disabled) {
        addSingleProductToCart(singleButton.dataset.bcuAddVariant, singleButton);
      }
      return;
    }

    const buildTrigger = event.target.closest("[data-sw-bundle-build]");
    if (!buildTrigger) return;
    const card = buildTrigger.closest("[data-sw-bundle-card]");
    if (!card) return;
    const sheet = getSheet(card);
    if (!sheet) return; // Kein Sheet im DOM → <a href> zur PDP greift
    const focusTarget = buildTrigger.matches("a, button")
      ? buildTrigger
      : card.querySelector("button[data-sw-bundle-build]");
    if (sheet.open(card, focusTarget)) event.preventDefault();
  });

  function initAll() {
    document.querySelectorAll("smellawear-collection [data-bqb-rail]").forEach(initRail);
  }

  document.readyState === "loading"
    ? document.addEventListener("DOMContentLoaded", initAll)
    : initAll();

  new MutationObserver(initAll).observe(document.documentElement, { childList: true, subtree: true });
})();
